import { call, all, put, takeLatest, select } from 'redux-saga/effects';

import api from '~/services/api';
import notification from '~/services/notification';
import history from '~/services/history';
import * as orderTypeActions from '~/store/modules/order-type/order-type-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM
} from '~/store/modules/order-type/order-type-constants';
import { ISA_ERROR, ORDER_TYPE } from '~/constants';
import { formatMessage } from '~/i18n/translate';
import { AppStateType } from '~/store/modules/root-reducer';
import domainService from '~/services/modules/domain/domain-service';
import orderTypeService from '~/store/modules/order-type/order-type-services';
import productService from '../product/product-service';

const INITIAL_VALUES = {
  externalCode: '',
  name: '',
  minimumValue: 0,
  maximumValue: 0,
  subsidiaries: [],
  paymentPlans: [],
  products: [],
  team: [],
  isActive: true
};

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a.externalCode - b.externalCode);
};

export function* getAll() {
  try {
    yield put(orderTypeActions.setLoadingData(true));

    const { data } = yield call(orderTypeService.getOrderTypes);
    if (data) {
      yield put(orderTypeActions.getAllSuccess([...data]));
    }

    yield put(orderTypeActions.setLoadingData(false));
  } catch (error) {
    yield put(orderTypeActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(orderTypeActions.setLoadingData(true));
    const { minimumOrderValue } = yield select(state => state.configuration);
    const user = yield select((state: AppStateType) => state.auth.user);

    const [{ data: subsidiaries }, { data: teams }, { data: paymentPlans }] = yield all([
      call(domainService.getSubsidiaries, {}),
      call(api.get, '/team'),
      call(domainService.getPaymentPlans)
    ]);

    const selects = {
      subsidiaries: sort(subsidiaries),
      teams: sort(teams),
      paymentPlans: sort(paymentPlans)
    };

    if (!id) {
      yield put(
        orderTypeActions.getByIdSuccess({
          data: {
            ...INITIAL_VALUES,
            minimumValue: minimumOrderValue ?? 0
          },
          selects
        })
      );
    } else {
      const { data } = yield call(orderTypeService.getOrderTypeById, id);

      if (data) {
        const { productExternalCodes } = data;
        let products = [];
        if (productExternalCodes?.length) {
          const { data: productsRaw } = yield call(productService.getRawProducts, {
            productExternalCodes: productExternalCodes.join(',')
          });
          if (productsRaw.length) {
            productsRaw.forEach(element => {
              const { externalCode, name } = element;
              products = [...products, { externalCode, name }];
            });
          }
        }
        yield put(
          orderTypeActions.getByIdSuccess({
            data: {
              ...data,
              subsidiaries: [...subsidiaries.filter(x => data?.subsidiaries?.includes(x.externalCode))],
              team: [...teams.filter(x => data?.team?.includes(x._id))],
              paymentPlans: [...paymentPlans.filter(x => data?.paymentPlanExternalCodes?.includes(x?.externalCode))],
              products
            },
            selects
          })
        );
      }
    }
    yield put(orderTypeActions.setLoadingData(false));
  } catch (error) {
    yield put(orderTypeActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    yield put(orderTypeActions.setLoadingSend(true));

    const { id, payload } = data;
    yield select((state: AppStateType) => state?.orderType?.form);

    const newPayload = {
      ...payload,
      type: payload?.type ?? ORDER_TYPE.NORMAL,
      paymentPlanExternalCodes: [...payload?.paymentPlans.map(x => x?.externalCode)],
      subsidiaries: [...payload?.subsidiaries.map(x => x.externalCode)],
      team: [...payload?.team.map(item => item._id)],
      productExternalCodes: [...payload?.products.map(item => item.externalCode)]
    };

    delete newPayload.paymentPlans;

    if (!id) {
      yield call(orderTypeService.createOrderType, newPayload);
    } else {
      yield call(orderTypeService.updateOrderType, newPayload);
    }

    yield call(
      notification.success,
      id ? formatMessage('orderTypeFormSuccessEdit') : formatMessage('orderTypeFormSuccessCreate')
    );
    yield put(orderTypeActions.setLoadingSend(false));
    yield call(history.push, '/register/order-type');
  } catch (error) {
    yield put(orderTypeActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(orderTypeActions.setLoadingSend(true));
      yield call(orderTypeService.deleteOrderType, id);
      yield put(orderTypeActions.setLoadingSend(false));

      yield put(orderTypeActions.getAllRequest());
      yield call(notification.success, formatMessage('orderTypeFormSuccessCreate'));
    }
  } catch (error) {
    yield put(orderTypeActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/order-type/edit/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem)
]);
